import { useEffect, useRef } from "react";
import Lenis from "lenis";
import { createLenis } from "../../lenis";
import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import type { JoinLayoutProps } from "../types";
import { WeddingProgram } from "../WeddingProgram";
import { DEMO_IMAGES } from "../mock-data";

gsap.registerPlugin(ScrollTrigger);

function formatDate(dateTime: string | null) {
  if (!dateTime) return { display: "-- · -- · ----", time: "" };
  const d = new Date(dateTime);
  const day = d.getDate().toString().padStart(2, "0");
  const month = (d.getMonth() + 1).toString().padStart(2, "0");
  const year = d.getFullYear();
  const time = d.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" });
  return { display: `${day} · ${month} · ${year}`, time };
}

export function MinimalLayout({ formSlot, program, title, dateTime, location, description, slug }: JoinLayoutProps) {
  const lenisRef = useRef<Lenis | null>(null);
  const date = formatDate(dateTime);

  useEffect(() => {
    window.scrollTo(0, 0);
    const lenis = createLenis({ duration: 1.6, easing: (t) => 1 - Math.pow(1 - t, 3) });
    lenis.scrollTo(0, { immediate: true });
    lenisRef.current = lenis;
    const ticker = (time: number) => lenis.raf(time * 1000);
    gsap.ticker.add(ticker);
    gsap.ticker.lagSmoothing(0);
    lenis.on("scroll", ScrollTrigger.update);

    const ctx = gsap.context(() => {
      // Hero title scale down on load
      gsap.from(".min-hero-title", {
        scale: 1.15,
        opacity: 0,
        duration: 1.6,
        ease: "power3.out",
      });
      gsap.from(".min-hero-sub", {
        opacity: 0,
        duration: 1,
        delay: 0.8,
        ease: "power2.out",
      });

      // CSS 3D ring card — rotates as you scroll
      const ringCard = document.querySelector<HTMLElement>(".min-ring-card");
      if (ringCard) {
        const obj = { angle: 0 };
        gsap.to(obj, {
          angle: 360,
          ease: "none",
          scrollTrigger: {
            trigger: ".min-ring-section",
            start: "top bottom",
            end: "bottom top",
            scrub: 1,
            onUpdate() {
              ringCard.style.transform = `perspective(800px) rotateY(${obj.angle}deg)`;
            },
          },
        });
      }

      // Big date numbers count up
      const dateEl = document.querySelector<HTMLElement>(".min-date-display");
      if (dateEl) {
        gsap.from(dateEl, {
          opacity: 0,
          scale: 0.8,
          duration: 1,
          ease: "back.out(1.4)",
          scrollTrigger: {
            trigger: ".min-date-section",
            start: "top 70%",
            toggleActions: "play reverse play reverse",
          },
        });
      }

      // Word-by-word story reveal
      const storyWords = document.querySelectorAll(".min-story-word");
      storyWords.forEach((word, i) => {
        gsap.from(word, {
          opacity: 0,
          duration: 0.4,
          delay: i * 0.015,
          ease: "power1.out",
          scrollTrigger: {
            trigger: ".min-story-section",
            start: "top 80%",
            toggleActions: "play reverse play reverse",
          },
        });
      });

      // Form fade in
      gsap.from(".min-form-wrapper", {
        opacity: 0,
        y: 40,
        duration: 1,
        ease: "power2.out",
        scrollTrigger: {
          trigger: ".min-form-wrapper",
          start: "top 80%",
          toggleActions: "play reverse play reverse",
        },
      });
    });

    return () => {
      lenis.destroy();
      gsap.ticker.remove(ticker);
      ScrollTrigger.killAll();
      ctx.revert();
    };
  }, []);

  const storyWords = (description ?? "Sizi özel günümüze bekliyoruz.")
    .split(" ")
    .filter(Boolean);

  return (
    <main className="bg-white text-neutral-900 overflow-x-hidden">
      {/* ── Hero ──────────────────────────────────────────────────────── */}
      <section className="min-h-screen flex flex-col items-center justify-center px-6 text-center">
        <p className="min-hero-sub text-xs tracking-[0.5em] uppercase text-neutral-400 mb-8">
          Düğün Davetiyesi
        </p>
        <h1 className="min-hero-title font-serif font-light leading-[0.9] text-[clamp(3rem,12vw,9rem)] tracking-tight text-neutral-900">
          {title}
        </h1>
        <div className="mt-10 w-12 h-px bg-neutral-300" />
        <p className="min-hero-sub mt-6 text-sm tracking-[0.3em] text-neutral-400 uppercase">
          {date.display}
        </p>
      </section>

      {/* ── CSS 3D Ring ──────────────────────────────────────────────── */}
      <section className="min-ring-section min-h-screen flex items-center justify-center bg-neutral-50 px-6">
        <div className="flex flex-col items-center gap-12">
          <p className="text-xs tracking-[0.4em] uppercase text-neutral-400">Yüzüklerimiz</p>

          {/* 3D rotating card */}
          <div
            className="min-ring-card relative w-64 h-64 sm:w-80 sm:h-80"
            style={{ transformStyle: "preserve-3d" }}
          >
            <div className="absolute inset-0 rounded-full overflow-hidden shadow-2xl">
              <img
                src={DEMO_IMAGES.ring}
                alt="Yüzük"
                className="w-full h-full object-cover"
              />
            </div>
            {/* Shimmer ring overlay */}
            <div className="absolute inset-0 rounded-full ring-1 ring-neutral-200" />
          </div>

          <p className="text-center text-sm text-neutral-400 max-w-xs leading-relaxed">
            Sonsuza dek birlikte — iki kalp, tek yüzük.
          </p>
        </div>
      </section>

      {/* ── Date Section ─────────────────────────────────────────────── */}
      <section className="min-date-section min-h-[60vh] flex items-center justify-center px-6 bg-white">
        <div className="text-center">
          <p className="text-xs tracking-[0.4em] uppercase text-neutral-300 mb-6">Tarih</p>
          <p className="min-date-display font-serif font-light text-[clamp(2.5rem,8vw,6rem)] text-neutral-900 tracking-tight">
            {date.display}
          </p>
          {date.time && (
            <p className="mt-4 text-lg tracking-widest text-neutral-400">{date.time}</p>
          )}
          {location && (
            <p className="mt-6 text-base text-neutral-500 max-w-sm mx-auto">{location}</p>
          )}
        </div>
      </section>

      {/* ── Story ────────────────────────────────────────────────────── */}
      <section className="min-story-section min-h-[70vh] flex items-center justify-center bg-neutral-50 px-6 py-24">
        <div className="max-w-2xl text-center">
          <p className="text-xs tracking-[0.4em] uppercase text-neutral-300 mb-10">Hikayemiz</p>
          <p className="text-xl sm:text-2xl font-serif font-light text-neutral-700 leading-relaxed">
            {storyWords.map((word, i) => (
              <span key={i} className="min-story-word inline-block mr-[0.25em] opacity-100">
                {word}
              </span>
            ))}
          </p>
        </div>
      </section>

      {/* ── Gallery strip ────────────────────────────────────────────── */}
      <section className="overflow-hidden bg-white py-16">
        <div className="flex gap-2 px-4">
          {DEMO_IMAGES.gallery.map((src, i) => (
            <div key={i} className="flex-shrink-0 w-48 h-64 sm:w-64 sm:h-80 overflow-hidden rounded">
              <img src={src} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
            </div>
          ))}
        </div>
      </section>

      {/* ── Form ─────────────────────────────────────────────────────── */}
      <section className="min-h-screen flex items-center justify-center bg-neutral-900 px-6 py-24">
        <div className="min-form-wrapper w-full max-w-md">
          <p className="text-center text-xs tracking-[0.4em] uppercase text-white/30 mb-4">Katılım</p>
          <h2 className="text-center text-3xl font-serif font-light text-white mb-10">
            Bize Katılın
          </h2>
          <WeddingProgram entries={program} className="mb-10" />
          {formSlot}
        </div>
      </section>
    </main>
  );
}
